import { SITE_LEVEL_PAGE_ID } from '@wix/a11y-wizard-common';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { CollapseList, StatusIcon, TaskListItem, ToggleType } from '@wix/rules-panel-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalysisState, usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../../hooks/use-a11y-wizard-adapter';
import { useGetRuleIdToLabelMap } from '../../../../../hooks/use-get-ruleId-to-label-map';
import { useQueryRuleExecutionResultsQuery } from '../../../../../queries/use-query-rule-execution-results-query';
import { isStaticPage } from '../../../../../rule-mappers/shared/helpers';
import { resolveGroupAnalysisState } from '../../../../../utils/resolve-group-analysis-state';
import { useStaticPageRuleResults } from '../../rule-groups/static-page-group/use-static-page-rule-results';
import { useGetPageRuleIds } from '../utils/use-get-page-rule-ids';
import { useShowFetchErrorModal } from '../utils/use-show-fetch-error-modal';
import { CollapseActionsProvider, ItemScopeProvider, useCloseSelf } from './context/collapse-actions-context';
import { useOpenNextItem } from './hooks/use-open-next-item';
import { RuleContentWithCollapse } from './rule-content-with-collapse';
import { useHandleUnsavedChangesOnToggle } from './utils/use-handle-unsaved-changes-on-toggle';
export const PageTaskList = /*#__PURE__*/React.memo(_ref => {
  let {
    pageId,
    isPageOpen,
    topComponent
  } = _ref;
  const showFetchErrorModal = useShowFetchErrorModal();
  const {
    refetch: refetchRuleExecutionResults
  } = useQueryRuleExecutionResultsQuery(pageId, () => showFetchErrorModal({
    cancelCallback: () => {
      closePage();
      setGroupData(pageId, {
        analysisState: AnalysisState.AnalyzeDonePendingResults
      });
    },
    tryAgainCallback: refetchRuleExecutionResults
  }));
  const closePage = useCloseSelf();
  const {
    getGroupData,
    setGroupData
  } = usePanelStateAtom();
  const groupData = getGroupData(pageId);
  if (!groupData) {
    throw new Error('Group data not found for page id: ' + pageId);
  }
  const {
    t
  } = useTranslation();
  const {
    analysisState,
    itemType
  } = groupData;
  const {
    isFetchResultSucceeded
  } = resolveGroupAnalysisState(analysisState);
  const ruleResults = useStaticPageRuleResults(pageId);
  const ruleIds = ruleResults.map(result => result.ruleId);
  const [openItems, setOpenItems] = useState(new Set());

  // Auto-fetch when group is open and results become pending
  useEffect(() => {
    if (isPageOpen && analysisState === AnalysisState.AnalyzeDonePendingResults && (isStaticPage(itemType) || pageId === SITE_LEVEL_PAGE_ID)) {
      refetchRuleExecutionResults();
    }
  }, [analysisState, isPageOpen, itemType, pageId, refetchRuleExecutionResults]);
  const {
    a11yWizardAdapter,
    capabilities
  } = useA11yWizardAdapter();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const ruleIdsForSkeleton = useGetPageRuleIds(pageId, ruleIds);
  const {
    pageLevelruleIdToLabelKeyMap
  } = useGetRuleIdToLabelMap();

  // Wraps setOpenItems to fire onRuleToggle(Collapse) for rules that were
  // implicitly evicted by CollapseList's singleOpenMode (i.e. a new item
  // was opened so the old one was silently removed). This runs *after*
  // useHandleUnsavedChangesOnToggle's navigation guard resolves, so a
  // cancelled unsaved-changes modal never triggers the side effect.
  const wrappedSetOpenItems = useCallback(newOpenItems => {
    const hasNewlyOpened = [...newOpenItems].some(id => !openItems.has(id));
    if (hasNewlyOpened) {
      openItems.forEach(openItemId => {
        if (!newOpenItems.has(openItemId)) {
          const closedIndex = ruleResults.findIndex(r => r.ruleId === openItemId);
          if (closedIndex !== -1) {
            var _ruleResults$closedIn, _ruleResults$closedIn2;
            (_ruleResults$closedIn = (_ruleResults$closedIn2 = ruleResults[closedIndex]).onRuleToggle) == null || _ruleResults$closedIn.call(_ruleResults$closedIn2, ToggleType.Collapse, closedIndex, false);
          }
        }
      });
    }
    setOpenItems(newOpenItems);
  }, [openItems, ruleResults, setOpenItems]);
  const handleSetOpenItems = useHandleUnsavedChangesOnToggle(openItems, wrappedSetOpenItems, () => a11yWizardAdapter.moveToPage(pageId));
  const ruleResultsForAutoExpand = useMemo(() => {
    return ruleResults.map(result => {
      return {
        id: result.ruleId,
        status: result.status
      };
    });
  }, [ruleResults]);
  const {
    handlePulseComplete
  } = useOpenNextItem({
    items: ruleResultsForAutoExpand,
    pageId,
    setOpenItems
  });
  const tasks = useMemo(() => {
    if (!isFetchResultSucceeded) {
      return ruleIdsForSkeleton == null ? void 0 : ruleIdsForSkeleton.map(ruleId => {
        return /*#__PURE__*/React.createElement(ItemScopeProvider, {
          id: ruleId,
          key: ruleId
        }, /*#__PURE__*/React.createElement(TaskListItem, {
          id: ruleId,
          isLoading: true,
          showBottomShadow: !compactPanelChrome,
          showTopShadow: !compactPanelChrome,
          title: t(pageLevelruleIdToLabelKeyMap[ruleId]),
          variant: "collapsible"
        }));
      });
    }
    return ruleResults.map((ruleResult, index, array) => {
      const isLast = index === array.length - 1;
      return /*#__PURE__*/React.createElement(ItemScopeProvider, {
        id: ruleResult.ruleId,
        key: ruleResult.ruleId
      }, /*#__PURE__*/React.createElement(TaskListItem, {
        id: ruleResult.ruleId,
        prefix: /*#__PURE__*/React.createElement(StatusIcon, {
          status: ruleResult.status,
          onComplete: handlePulseComplete
        }),
        showBottomShadow: !isLast && !compactPanelChrome,
        showTopShadow: !compactPanelChrome,
        title: ruleResult.label,
        variant: "collapsible",
        onToggle: (_id, isOpen) => {
          ruleResult.onRuleToggle == null || ruleResult.onRuleToggle(isOpen ? ToggleType.Expand : ToggleType.Collapse, index, true // initiatedByUser
          );
        }
      }, /*#__PURE__*/React.createElement(Box, {
        dataHook: "page-task-list-item-content",
        padding: "SP3 SP6 SP6 SP6"
      }, /*#__PURE__*/React.createElement(RuleContentWithCollapse, {
        ruleResult: ruleResult
      }))));
    });
  }, [isFetchResultSucceeded, ruleResults, ruleIdsForSkeleton, compactPanelChrome, t, pageLevelruleIdToLabelKeyMap, handlePulseComplete]);
  return /*#__PURE__*/React.createElement(CollapseActionsProvider, {
    setOpenItems: handleSetOpenItems
  }, /*#__PURE__*/React.createElement(CollapseList, {
    autoScroll: true,
    contentGap: 0,
    openItems: openItems,
    setOpenItems: handleSetOpenItems
  }, topComponent, tasks));
});
//# sourceMappingURL=page-task-list.js.map